import React, { useState, useEffect, useRef } from 'react';
import { Box, Tooltip, useTheme, CircularProgress } from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as SpreadsheetIcon,
  Code as CodeIcon,
  FolderZip as ZipIcon,
  Image as ImageIcon,
  ErrorOutline as RagErrorIcon,
  WarningAmber as RagWarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FileAttachment } from '../../hooks/useChatMessages';
import threadService from '../../services/threadService';

interface FileThumbnailProps {
  file: FileAttachment;
  threadId?: string;
  onDownload?: (file: FileAttachment) => void;
  /** #157: traduce file.rag_error a un mensaje amigable (inyectado por MessageAttachments). */
  resolveRagError?: (rawError?: string | null) => { message: string; retryable: boolean };
}

/**
 * Componente que muestra una miniatura de un archivo adjunto.
 * Soporta:
 * - Imágenes: Muestra thumbnail o icono
 * - PDFs: Icono rojo característico
 * - Documentos: Icono azul (Word, TXT)
 * - Hojas de cálculo: Icono verde (Excel, CSV)
 * - Código: Icono naranja (JSON)
 * - Comprimidos: Icono morado (ZIP, RAR)
 * - Otros: Icono genérico gris
 */
const FileThumbnail: React.FC<FileThumbnailProps> = ({ file, threadId, onDownload, resolveRagError }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // #157: estado de ingesta RAG del fichero (surfaced desde file.rag_status en getMessages).
  // Sin estado (adjuntos antiguos / completed) → no se muestra indicador.
  const ragFailed = file.ragStatus === 'error';
  // 'pending' (aún sin empezar) y 'processing' (en curso, live #274) → spinner.
  const ragProcessing = file.ragStatus === 'pending' || file.ragStatus === 'processing';
  // #274 — aviso NO-fatal en un fichero completado (p.ej. imágenes sin describir): badge ámbar.
  const ragWarning = file.ragStatus === 'completed' && (file.warningCount || 0) > 0;
  const ragErrorMessage = ragFailed
    ? (resolveRagError
        ? resolveRagError(file.ragError).message
        : t('chat.fileNotProcessed', { defaultValue: 'No se pudo procesar el fichero' }))
    : null;
  const statusTooltip = ragFailed
    ? `${file.fileName} — ${ragErrorMessage}`
    : ragProcessing
      ? `${file.fileName} — ${t('chat.fileProcessing', { defaultValue: 'Procesando…' })}`
      : ragWarning
        ? `${file.fileName} — ${file.lastWarning || t('chat.fileWarning', { defaultValue: 'Procesado con avisos' })}`
        : file.fileName;

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  const [refreshedThumbnailUrl, setRefreshedThumbnailUrl] = useState<string | null>(null);
  const [previousThumbnailUrl, setPreviousThumbnailUrl] = useState<string | null>(null);
  const previousUrlRef = useRef<string | null>(null);

  // Validación robusta de tipos de archivo
  const fileType = file.fileType || 'application/octet-stream';
  const isImage = fileType.startsWith('image/');
  const isPdf = fileType === 'application/pdf';
  const isDoc = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ].includes(fileType);
  const isSpreadsheet = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ].includes(fileType);
  const isCode = fileType === 'application/json' || fileType === 'text/javascript';
  const isZip = [
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed'
  ].includes(fileType);

  /**
   * Formatea el tamaño del archivo en formato legible
   * @param bytes - Tamaño en bytes
   * @returns String formateado (ej: "2.5 MB")
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  /**
   * Retorna el icono apropiado según el tipo de archivo
   * Usa tokens semánticos del tema para colores consistentes
   */
  const fileIconColors = (theme as any).fileIcons;
  const getFileIcon = () => {
    if (isPdf) return <PdfIcon sx={{ fontSize: 48, color: fileIconColors?.pdf || theme.palette.error.main }} />;
    if (isDoc) return <DocIcon sx={{ fontSize: 48, color: fileIconColors?.document || theme.palette.info.main }} />;
    if (isSpreadsheet) return <SpreadsheetIcon sx={{ fontSize: 48, color: fileIconColors?.spreadsheet || theme.palette.success.main }} />;
    if (isCode) return <CodeIcon sx={{ fontSize: 48, color: fileIconColors?.code || theme.palette.warning.main }} />;
    if (isZip) return <ZipIcon sx={{ fontSize: 48, color: fileIconColors?.archive || theme.palette.secondary.main }} />;
    if (isImage) return <ImageIcon sx={{ fontSize: 48, color: fileIconColors?.image || theme.palette.info.main }} />;
    return <FileIcon sx={{ fontSize: 48, color: fileIconColors?.generic || theme.palette.grey[500] }} />;
  };

  /**
   * Maneja el click en la miniatura para abrir el archivo
   */
  const handleClick = () => {
    if (file.fileUrl) {
      window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Maneja errores al cargar la imagen
   * Intenta refrescar la URL si no se ha intentado antes y tenemos threadId
   */
  const handleImageError = async () => {
    console.warn(`[FileThumbnail] Error loading thumbnail for: ${file.fileName}`);

    // Intentar refrescar URL si no lo hemos intentado antes y tenemos threadId
    if (!hasTriedRefresh && threadId) {
      setHasTriedRefresh(true);
      console.log(`[FileThumbnail] Attempting to refresh URL for file: ${file.id}`);

      try {
        // apiClient already unwraps { success, data } -> data
        const result = await threadService.refreshFileUrl(threadId, file.id);
        if (result?.thumbnailUrl) {
          console.log(`[FileThumbnail] URL refreshed successfully for file: ${file.id}`);
          setRefreshedThumbnailUrl(result.thumbnailUrl);
          setImageError(false); // Reset error to try loading again
          setImageLoaded(false); // Reset loaded state
          return; // Will re-render with new URL
        }
      } catch (error) {
        console.error(`[FileThumbnail] Failed to refresh URL for file: ${file.id}`, error);
      }
    }

    // Si no pudimos refrescar o ya lo intentamos, mostrar error
    setImageError(true);
  };

  /**
   * Determina si debe mostrar la imagen o el icono
   * Usa la URL refrescada si está disponible, sino la original
   * Muestra thumbnail si existe (imágenes, PDFs con preview, etc.)
   */
  const thumbnailUrlToUse = refreshedThumbnailUrl || file.thumbnailUrl;
  const shouldShowImage = thumbnailUrlToUse && !imageError;

  /**
   * Efecto para detectar cambios de URL y preservar la imagen anterior
   * Esto elimina el flicker al actualizar de thumbnail cliente → servidor
   *
   * ✅ FIX: Preserva la URL anterior SIEMPRE, sin importar si imageLoaded=true
   * Esto previene el race condition cuando el backend responde muy rápido
   * y la URL cambia antes de que el thumbnail del cliente termine de cargar
   */
  useEffect(() => {
    // Si la URL cambió respecto a la anterior
    if (thumbnailUrlToUse && thumbnailUrlToUse !== previousUrlRef.current) {
      // ✅ FIX: Guardar SIEMPRE la URL anterior si existe (sin verificar imageLoaded)
      // Esto asegura transición suave incluso cuando el backend es muy rápido
      if (previousUrlRef.current) {
        setPreviousThumbnailUrl(previousUrlRef.current); // Guardar para mostrar durante carga
        setImageLoaded(false); // Reiniciar estado de carga
      }
      // Actualizar ref con la nueva URL
      previousUrlRef.current = thumbnailUrlToUse;
    }
  }, [thumbnailUrlToUse]); // ✅ FIX: Eliminar imageLoaded de dependencias (causa re-renders innecesarios)

  return (
    <Box
      sx={{
        position: 'relative',
        width: 96,
        height: 96,
        borderRadius: 1.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: ragFailed ? 'error.main' : 'divider',   // #157: tinte rojo si la ingesta falló
        backgroundColor: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        cursor: file.fileUrl ? 'pointer' : 'default',
        '&:hover': file.fileUrl ? {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        } : {},
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Abrir archivo ${file.fileName}`}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && file.fileUrl) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Thumbnail/Icon Area - Full height */}
      <Tooltip title={statusTooltip} arrow placement="top">
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: shouldShowImage ? 'transparent' : 'action.hover',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {shouldShowImage ? (
            <>
              {/* Imagen anterior (fallback) - se muestra mientras carga la nueva */}
              {!imageLoaded && previousThumbnailUrl && (
                <img
                  src={previousThumbnailUrl}
                  alt={file.fileName}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}

              {/* Placeholder mientras carga - NO mostrar icono porque hay thumbnail válido cargando
                  El icono solo debe verse para archivos SIN thumbnail (ZIP, etc.) en el bloque else */}
              {!imageLoaded && !previousThumbnailUrl && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'action.hover',
                  }}
                />
              )}

              {/* Imagen nueva - transición suave */}
              <img
                src={thumbnailUrlToUse}
                alt={file.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                }}
                onLoad={() => {
                  setImageLoaded(true);
                  // ✅ FIX: No limpiar previousThumbnailUrl con setTimeout
                  // La limpieza prematura causaba flicker si S3 tardaba > 200ms
                  // El estado se resetea naturalmente cuando cambia la URL (useEffect)
                }}
                onError={handleImageError}
              />
            </>
          ) : (
            getFileIcon()
          )}
        </Box>
      </Tooltip>

      {/* File Size Badge - Using native span to avoid MUI style conflicts */}
      <span
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          padding: '1px 4px',
          borderRadius: 4,
          fontSize: '0.55rem',
          fontWeight: 700,
          zIndex: 10,
        }}
      >
        {formatFileSize(file.fileSize)}
      </span>

      {/* #157/#274: badge de estado de ingesta RAG (top-left) — reusa el patrón de overlay.
          error → icono rojo + borde rojo + motivo en el tooltip; pending/processing → spinner;
          completed con avisos → icono ámbar; completed limpio/sin-estado → nada (sin ruido visual). */}
      {(ragFailed || ragProcessing || ragWarning) && (
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            left: 2,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: 'background.paper',
            boxShadow: 1,
          }}
          aria-label={
            ragFailed
              ? (ragErrorMessage || undefined)
              : ragWarning
                ? (file.lastWarning || t('chat.fileWarning', { defaultValue: 'Procesado con avisos' }))
                : t('chat.fileProcessing', { defaultValue: 'Procesando…' })
          }
        >
          {ragFailed
            ? <RagErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
            : ragWarning
              ? <RagWarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
              : <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary' }} />}
        </Box>
      )}
    </Box>
  );
};

export default FileThumbnail;
